import React, { memo } from 'react';
import { MoveKind, QUADRANT_HALF_SIZE, QuadrantState, apply, childOffset, compose, inverse, orientationElement } from '../geometry';
import { rotateQuadrant } from '../ultrarubigramUtils';
import RotateButton from './RotateButton';
import Subgrid, { SubgridLayer } from './Subgrid';

type QuadrantProps = {
  g: number;
  q: number;
  quadrant: QuadrantState;
  /** Screen orientation of the grid this quadrant sits in. */
  parent: number;
  /** Screen position of that grid's center, in half-cells from the puzzle center. */
  parentCenterX: number;
  parentCenterY: number;
  interactive: boolean;
  /** Render one of the copies of the piece tree instead: the border bands, or one kind of tab (see Subgrid). */
  layer?: SubgridLayer;
  /** The move the grid this quadrant sits in is making, if any. */
  ancestorMoving: MoveKind | null;
};

const Quadrant = memo(({
  g, q, quadrant, parent, parentCenterX, parentCenterY, interactive, layer, ancestorMoving,
}: QuadrantProps) => {
  const self = compose(parent, orientationElement(quadrant));

  const [offsetX, offsetY] = apply(parent, ...childOffset(q, QUADRANT_HALF_SIZE));
  const centerX = parentCenterX + offsetX;
  const centerY = parentCenterY + offsetY;

  const flipperStyle: React.CSSProperties = {
    transform: `rotateY(${quadrant.flipH ? 180 : 0}deg)`
      + ` rotateX(${quadrant.flipV ? 180 : 0}deg)`
      + ` rotateZ(${quadrant.rot}deg)`,
  };

  // A tab copy's contents are where a tab's two quarters find each other, to highlight as one.
  const contentsClasses = 'ultraContents'
    + (layer && layer !== 'bands' ? ' quadrantContents' : '')
    + (quadrant.isFlipping ? ' isFlipping' : '')
    + (quadrant.moving ? ' isMoving' : '');
  const moving = quadrant.moving || quadrant.subgrids.some(subgrid => subgrid.moving);
  const pieceClasses = `ultraPiece ultraQuadrant pos${q}${moving ? ' moving' : ''}`
    + (layer === 'bands' ? ' bands' : layer ? ' tabs' : '');

  return (
    <div className={pieceClasses}>
      <div className="ultraFlipper" style={flipperStyle}>
        <div className={contentsClasses}>
          {quadrant.subgrids.map((subgrid, s) => (
            <Subgrid
              key={s}
              g={g}
              q={q}
              s={s}
              subgrid={subgrid}
              parent={self}
              parentCenterX={centerX}
              parentCenterY={centerY}
              interactive={interactive}
              layer={layer}
              ancestorMoving={quadrant.moving ?? ancestorMoving}
            />
          ))}

          {interactive && !layer &&
            <RotateButton level="quadrant" upright={inverse(self)} onRotate={cw => rotateQuadrant(g, q, cw)}/>}
        </div>
      </div>
    </div>
  );
});

export default Quadrant;
